import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle, Circle, BookOpenText, ClipboardText, FilePdf } from '@phosphor-icons/react';
import { Md } from '@/components/Md';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProgramaPage() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [mealTypes, setMealTypes] = useState([]);
  const [content, setContent] = useState(null);
  const [progress, setProgress] = useState({ completed_items: [], confirmed: false });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('plano');

  const fetchAll = useCallback(async () => {
    try {
      const tag = user?.patient_type || 'generico';
      const [planRes, mtRes, contentRes, progressRes] = await Promise.all([
        axios.get(`${API}/api/meal-plans?client_id=${user?.user_id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/meal-types`).catch(() => ({ data: { types: [] } })),
        axios.get(`${API}/api/program-onboarding/${tag}`).catch(() => ({ data: null })),
        axios.get(`${API}/api/my-program-progress`).catch(() => ({ data: {} })),
      ]);
      const activePlan = (planRes.data || []).find(p => p.status === 'ativo') || (planRes.data || [])[0];
      setPlan(activePlan || null);
      setMealTypes(mtRes.data?.types || []);
      setContent(contentRes.data);
      setProgress(progressRes.data || {});
    } catch {} finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Switch the active version for today (client-side)
  const switchVersion = async (vid) => {
    if (!plan?.id || !vid || vid === plan.active_version_id) return;
    try {
      await axios.put(`${API}/api/meal-plans/${plan.id}/active-version`, { version_id: vid });
      const r = await axios.get(`${API}/api/meal-plans?client_id=${user?.user_id}&version_id=${encodeURIComponent(vid)}`);
      const fresh = (r.data || []).find(p => p.id === plan.id);
      if (fresh) setPlan(fresh);
      toast.success('Versão activa para hoje');
    } catch { toast.error('Erro ao mudar de versão'); }
  };

  const toggleItem = async (itemKey) => {
    const completed = progress.completed_items || [];
    const newCompleted = completed.includes(itemKey) ? completed.filter(k => k !== itemKey) : [...completed, itemKey];
    setProgress(prev => ({ ...prev, completed_items: newCompleted }));
    await axios.post(`${API}/api/my-program-progress`, { completed_items: newCompleted });
  };

  const handleConfirm = async () => {
    await axios.post(`${API}/api/my-program-progress`, { confirm: true, completed_items: progress.completed_items });
    setProgress(prev => ({ ...prev, confirmed: true, confirmed_at: new Date().toISOString() }));
    toast.success('Obrigada pela confirmação!');
  };

  const downloadPdf = async () => {
    if (!plan) return;
    try {
      // Passa sempre o version_id da versão ativa (a que a cliente vê "hoje"), para o PDF nunca
      // ficar desatualizado em relação à versão do plano que está a ser mostrada na app.
      const vq = plan.active_version_id ? `&version_id=${encodeURIComponent(plan.active_version_id)}` : '';
      const r = await axios.get(`${API}/api/meal-plans/${plan.id}/pdf?version=client${vq}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `plano_${plan.name?.replace(/ /g, '_')}.pdf`; a.click();
    } catch { toast.error('Erro ao gerar PDF'); }
  };

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  const sections = content?.sections || [];
  const allItems = sections.flatMap((s, si) => s.items.map((_, ii) => `${si}-${ii}`));
  const completedCount = (progress.completed_items || []).length;
  const totalCount = allItems.length;
  const MEAL_LABELS = {};
  mealTypes.forEach(m => { MEAL_LABELS[m.key] = m.label; });

  const hasOnboarding = sections.length > 0;

  return (
    <div className="fade-in" data-testid="programa-page">
      <h2 className="font-heading font-medium text-xl text-brand-text mb-4">O teu plano</h2>

      {/* Plan view - always shown */}
      <div>
          {!plan ? (
            <div className="text-center py-16 bg-white border border-brand-border rounded-2xl">
              <ClipboardText size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
              <p className="text-brand-muted text-sm">Ainda não tens um plano alimentar atribuído.</p>
              <p className="text-brand-placeholder text-xs mt-1">A tua nutricionista irá criar um plano personalizado para ti.</p>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading font-medium text-brand-text">{plan.name}</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${plan.status === 'ativo' ? 'bg-brand-mint/20 text-brand-green' : 'bg-brand-bg text-brand-muted'}`}>{plan.status}</span>
                </div>
                <button onClick={downloadPdf} className="border border-brand-border text-brand-green rounded-full px-3 py-1.5 text-xs flex items-center gap-1 hover:bg-brand-green/5">
                  <FilePdf size={14} /> PDF
                </button>
              </div>

              {/* Version selector (only when 2+ versions) */}
              {Array.isArray(plan.versions) && plan.versions.length >= 2 && (
                <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="programa-version-pills">
                  <span className="text-[11px] text-brand-muted mr-1">Versão de hoje:</span>
                  {plan.versions.map(v => (
                    <button
                      key={v.version_id}
                      onClick={() => switchVersion(v.version_id)}
                      className={`px-3 py-1.5 rounded-full text-xs transition-all ${plan.active_version_id === v.version_id ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border hover:border-brand-green'}`}
                      data-testid={`programa-version-${v.version_id}`}
                    >{v.label}</button>
                  ))}
                </div>
              )}

              {/* Meals */}
              <div className="space-y-4">
                {Object.entries(plan.meals || {}).filter(([k, v]) => !k.endsWith('_notes') && Array.isArray(v) && v.length > 0).sort(([a], [b]) => {
                  const ca = plan.meal_config?.[a] || {};
                  const cb = plan.meal_config?.[b] || {};
                  return (ca.time || '99').localeCompare(cb.time || '99');
                }).map(([mk, foods]) => {
                  const cfg = plan.meal_config?.[mk] || {};
                  const label = cfg.label || MEAL_LABELS[mk] || mk.replace(/_/g, ' ');
                  const time = cfg.time || '';
                  const notes = plan.meals?.[`${mk}_notes`] || '';
                  return (
                    <div key={mk} className="bg-white border border-brand-border rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-heading font-medium text-sm text-brand-green">{label}</h3>
                        {time && <span className="text-[10px] text-brand-muted">{time}</span>}
                      </div>
                      {(() => {
                        // Group by section (ex.: Sopa/Prato/Sobremesa) — mesmo campo `section` usado
                        // no editor (PlanosPage.js) e no PDF (meal_plans_pdf.py), para que a app e o
                        // PDF mostrem sempre a mesma estrutura de subgrupos.
                        const sections = {};
                        foods.forEach((f, i) => {
                          const sec = f.section || '';
                          if (!sections[sec]) sections[sec] = [];
                          sections[sec].push({ f, i });
                        });
                        const sectionKeys = Object.keys(sections);
                        const hasAnySections = sectionKeys.some(s => s !== '');
                        return sectionKeys.map(sec => (
                          <div key={sec || '__none'}>
                            {(hasAnySections && sec) && (
                              <p className="text-[10px] font-semibold text-brand-green uppercase tracking-wider mt-2 mb-1" data-testid={`meal-section-${mk}-${sec}`}>{sec}</p>
                            )}
                            {sections[sec].map(({ f, i }) => (
                              <div key={i} className="mb-1" data-testid={`meal-food-${mk}-${i}`}>
                                <p className="text-sm text-brand-text">{f.display_name || f.food_name} ({f.quantity}{f.unit})</p>
                                {f.equivalents?.length > 0 && f.equivalents.map((eq, ei) => (
                                  <p key={ei} className="text-xs text-brand-muted ml-3">ou {eq.measure_display ? `${eq.measure_display} de ` : ''}{eq.food_name}{eq.quantity ? ` (${eq.quantity}g)` : ''}</p>
                                ))}
                              </div>
                            ))}
                          </div>
                        ));
                      })()}
                      {notes && <Md as="p" className="text-xs text-brand-muted mt-2 pt-2 border-t border-brand-border/50 italic">{notes}</Md>}
                    </div>
                  );
                })}
              </div>

              {/* Recommendations */}
              {plan.recommendations?.length > 0 && (
                <div className="mt-4 bg-white border border-brand-border rounded-2xl p-4">
                  <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-2">Recomendações</h3>
                  {plan.water_goal && <p className="text-sm text-brand-text mb-1">Água: {plan.water_goal}L por dia</p>}
                  {plan.recommendations.map((r, i) => <p key={i} className="text-sm text-brand-text mb-1">. {r}</p>)}
                </div>
              )}
            </div>
          )}
        </div>
    </div>
  );
}
